import type { GamePhase, RuntimeState } from "./types.ts";
import { PHASE_BUILD, PHASE_PAUSED } from "./types.ts";

/**
 * BL-98 / D-230 — the RuntimeState write seam.
 *
 * `RuntimeState` (`game/types.ts`) is the live run-state object. Before this
 * module its run-vital fields (`gold`/`lives`/`wave`/`phase` + the fields written
 * atomically beside a phase transition) were mutated raw — `gs.<field> = …` —
 * scattered across ui / game-handlers / systems (S115-F23: the unencapsulated
 * god-object). These named intent methods are the single WRITE seam for those
 * vitals: every cross-module mutation now goes through one of them, so the set
 * of places a run-vital can change is greppable and bounded.
 *
 * Contract (the law these must obey — BL-98 is behavior-preserving):
 *  - each function is an EXACT relocation of the write it replaces — same field,
 *    same value expression, same order relative to its neighbours; never a
 *    reorder, coalesce, clamp-move, or "improvement".
 *  - READS stay direct (`gs.lives`, `gs.phase`, …) — only writes are seamed.
 *  - no EventBus emission lives here: emissions stay at the call-site so their
 *    order (which the golden master witnesses) is untouched.
 *
 * Lives-clamp note: the floor/ceil expressions (`Math.max(1, …)` penalties,
 * `Math.min(maxLives, …)` restores) stay at the call-site as plain READS; only
 * the resulting assignment is routed through `setLives`.
 *
 * Layer: this is the `game` layer (it owns `RuntimeState`); `ui`/`game`/`systems`
 * callers import DOWN/ACROSS into it — `core` never does (it can't see
 * `RuntimeState`), so `layer-direction.test.ts` stays green.
 *
 * Each intent takes the MINIMAL `Pick` of the fields it touches, so it accepts
 * both the full `RuntimeState` and the narrow `Pick<…>` slices the handler-dep
 * contexts carry (`handlers/types.ts`).
 *
 * Out of scope here (deliberate — see D-230 + BL-123): the lifecycle writers
 * (`createRuntimeState` factory + `resetForNewRun`) that set every field to its
 * initial value — they are the canonical state owner, not scattered mutation —
 * and the view/selection/modifier-flag long tail.
 */

// ─── Economy (gold) ──────────────────────────────────────────────────────────
// `gs.gold` is a per-frame MIRROR of `EconomySystem.currentGold` (the authority
// for spend/credit transactions); these sites only sync the mirror to a freshly
// authoritative total — hence `syncGold`, not spend/credit.
export function syncGold(gs: Pick<RuntimeState, "gold">, total: number): void {
  gs.gold = total;
}

// ─── Lifecycle / phase ────────────────────────────────────────────────────────
/** Set the run phase (build / combat / draft / cinematic / gameOver). */
export function setPhase(
  gs: Pick<RuntimeState, "phase">,
  phase: GamePhase,
): void {
  gs.phase = phase;
}

/** Enter PAUSED, remembering the phase to return to (the resume seam reads it). */
export function enterPause(
  gs: Pick<RuntimeState, "phase" | "previousPhase">,
): void {
  gs.previousPhase = gs.phase;
  gs.phase = PHASE_PAUSED;
}

/** Resume from PAUSED to the remembered phase (BUILD if none), clearing it. */
export function exitPause(
  gs: Pick<RuntimeState, "phase" | "previousPhase">,
): void {
  gs.phase = gs.previousPhase ?? PHASE_BUILD;
  gs.previousPhase = null;
}

// ─── Wave ─────────────────────────────────────────────────────────────────────
/** Set the current wave number (driven off the `wave.started` payload). */
export function setWave(gs: Pick<RuntimeState, "wave">, wave: number): void {
  gs.wave = wave;
}

/** Mark a wave as active (true on start) / inactive (false on completion). */
export function setWaveActive(
  gs: Pick<RuntimeState, "waveActive">,
  active: boolean,
): void {
  gs.waveActive = active;
}

// ─── Lives (integrity) ────────────────────────────────────────────────────────
// Clamp logic (`Math.max(1, …)` penalty floors, `Math.min(maxLives, …)` restore
// ceilings) stays at the call-site as a READ; only the resulting value is set.
/** Add integrity (operator/module/card grants). */
export function gainLives(gs: Pick<RuntimeState, "lives">, n: number): void {
  gs.lives += n;
}

/** Subtract integrity on a leak (unclamped — may cross 0 to trigger game-over). */
export function loseLives(gs: Pick<RuntimeState, "lives">, n: number): void {
  gs.lives -= n;
}

/** Set integrity to an already-computed value (failsafe restore, clamped grants). */
export function setLives(gs: Pick<RuntimeState, "lives">, lives: number): void {
  gs.lives = lives;
}

// ─── View / selection / input (BL-123) ─────────────────────────────────────────
// The non-vital long tail (BL-98 deferred → BL-123): the view/selection, payload,
// countdown, time-warp and card/module modifier flags. Same seam, same law as the
// vitals above — each function is an EXACT relocation of the write it replaces
// (same field, same value expression, same order beside its neighbours); reads
// stay direct; no emission moves here. The lifecycle owner (`createRuntimeState` /
// `resetForNewRun`) still writes the initial values raw — by design, not debt.

/** Select a placed tower for the range/HUD panel, or clear it (`null`). */
export function setSelectedTower(
  gs: Pick<RuntimeState, "selectedTower">,
  tower: RuntimeState["selectedTower"],
): void {
  gs.selectedTower = tower;
}

/** Set (or clear) the tower under the cursor for the hover range indicator (CR-28). */
export function setHoveredTower(
  gs: Pick<RuntimeState, "hoveredTower">,
  tower: RuntimeState["hoveredTower"],
): void {
  gs.hoveredTower = tower;
}

/** Set the placement mode (tower-ghost vs firewall-ghost). */
export function setPlacementMode(
  gs: Pick<RuntimeState, "placementMode">,
  mode: RuntimeState["placementMode"],
): void {
  gs.placementMode = mode;
}

/** Set (or clear) the armed tower element for placement. */
export function setSelectedTowerType(
  gs: Pick<RuntimeState, "selectedTowerType">,
  type: RuntimeState["selectedTowerType"],
): void {
  gs.selectedTowerType = type;
}

/** Set the combat targeting mode (default vs fastest-first). */
export function setTargetingMode(
  gs: Pick<RuntimeState, "targetingMode">,
  mode: RuntimeState["targetingMode"],
): void {
  gs.targetingMode = mode;
}

// ─── Payload targeting (BL-70) ──────────────────────────────────────────────────
/** Enter aim mode for a targeted payload (the mode + the card being aimed). */
export function setPayloadTargeting(
  gs: Pick<RuntimeState, "payloadTargetingMode" | "payloadTargetingCard">,
  mode: RuntimeState["payloadTargetingMode"],
  card: RuntimeState["payloadTargetingCard"],
): void {
  gs.payloadTargetingMode = mode;
  gs.payloadTargetingCard = card;
}

/** Clear payload aim mode (ESC cancel / resolve / re-arm) — both fields to null. */
export function clearPayloadTargeting(
  gs: Pick<RuntimeState, "payloadTargetingMode" | "payloadTargetingCard">,
): void {
  gs.payloadTargetingMode = null;
  gs.payloadTargetingCard = null;
}

// ─── Wave countdown (CR-8 / CR-6) ───────────────────────────────────────────────
/** Set the pre-wave countdown timer (ms remaining; 0 = inactive). */
export function setWaveCountdown(
  gs: Pick<RuntimeState, "waveCountdown">,
  ms: number,
): void {
  gs.waveCountdown = ms;
}

/** Advance the countdown by elapsed time (the per-frame decrement). */
export function tickWaveCountdown(
  gs: Pick<RuntimeState, "waveCountdown">,
  dt: number,
): void {
  gs.waveCountdown -= dt;
}

/** Set the disarm guard that stops auto-start re-firing after a manual start. */
export function setWaveStartedThisCountdown(
  gs: Pick<RuntimeState, "waveStartedThisCountdown">,
  started: boolean,
): void {
  gs.waveStartedThisCountdown = started;
}

// ─── Time-warp (payload) ────────────────────────────────────────────────────────
/** Set whether the time-warp payload is armed/available. */
export function setTimeWarpAvailable(
  gs: Pick<RuntimeState, "timeWarpAvailable">,
  available: boolean,
): void {
  gs.timeWarpAvailable = available;
}

/** Set the time-warp effect duration (seconds). */
export function setTimeWarpDuration(
  gs: Pick<RuntimeState, "timeWarpDuration">,
  seconds: number,
): void {
  gs.timeWarpDuration = seconds;
}

// ─── Card / module modifier flags ───────────────────────────────────────────────
/** Arm the Recursive-Loop skill (every Nth kill deals AoE); resets the counter. */
export function setRecursiveLoop(
  gs: Pick<RuntimeState, "recursiveLoopThreshold" | "recursiveLoopCounter">,
  threshold: number,
): void {
  gs.recursiveLoopThreshold = threshold;
  gs.recursiveLoopCounter = 0;
}

/** Advance the Recursive-Loop kill counter by one (per qualifying kill). */
export function incrementRecursiveLoopCounter(
  gs: Pick<RuntimeState, "recursiveLoopCounter">,
): void {
  gs.recursiveLoopCounter++;
}

/** Reset the Recursive-Loop kill counter (after an AoE trigger fires). */
export function resetRecursiveLoopCounter(
  gs: Pick<RuntimeState, "recursiveLoopCounter">,
): void {
  gs.recursiveLoopCounter = 0;
}

/** Set the firewall-slow card effect (active flag + slow factor). */
export function setFirewallSlow(
  gs: Pick<RuntimeState, "firewallSlowActive" | "firewallSlowFactor">,
  active: boolean,
  factor: number,
): void {
  gs.firewallSlowActive = active;
  gs.firewallSlowFactor = factor;
}

/** Toggle the show-all-HP-bars card effect. */
export function setShowAllHPBars(
  gs: Pick<RuntimeState, "showAllHPBars">,
  show: boolean,
): void {
  gs.showAllHPBars = show;
}

/** Set the max-integrity cap (GLASS_PROTOCOL trade-off; the floor/ceil clamp
 *  stays at the call-site as a READ — only the resulting value is set). */
export function setMaxLives(
  gs: Pick<RuntimeState, "maxLives">,
  maxLives: number,
): void {
  gs.maxLives = maxLives;
}

/** Set the CASCADE card effect (active flag + splash damage). */
export function setCascade(
  gs: Pick<RuntimeState, "cascadeActive" | "cascadeDamage">,
  active: boolean,
  damage: number,
): void {
  gs.cascadeActive = active;
  gs.cascadeDamage = damage;
}
